import React, { useEffect } from 'react';
import { useNavigate, Outlet, useLocation } from 'react-router-dom';
import { isAuthenticated, hasRole, hasPermission } from '../../services/authService';

/**
 * ProtectedRoute component for protecting routes that require authentication
 * @param {Object} props - Component props
 * @param {String} props.requiredRole - Role required to access the route (optional)
 * @param {String} props.requiredPermission - Permission required to access the route (optional)
 * @returns {JSX.Element} ProtectedRoute component
 */
const ProtectedRoute = ({ requiredRole, requiredPermission }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAuth = isAuthenticated();
  
  useEffect(() => {
    // If not authenticated, redirect to login (preserve return URL)
    if (!isAuth) {
      navigate(`/login?redirect=${encodeURIComponent(location.pathname + location.search)}`, { replace: true });
      return;
    }
    
    // If a specific role is required, check if the user has that role
    if (requiredRole && !hasRole(requiredRole)) {
      // Redirect to dashboard if user doesn't have the required role
      navigate('/', { replace: true });
      return;
    }
    
    // If a specific permission is required, check if the user has that permission
    if (requiredPermission && !hasPermission(requiredPermission)) {
      // Redirect to dashboard if user doesn't have the required permission
      navigate('/', { replace: true });
      return;
    }
  }, [isAuth, requiredRole, requiredPermission, navigate, location.pathname, location.search]);
  
  // If not authenticated, don't render anything (redirect will happen)
  if (!isAuth) {
    return null;
  }
  
  // If a specific role is required, check if the user has that role
  if (requiredRole && !hasRole(requiredRole)) {
    return null; // Redirect will happen in useEffect
  }
  
  // If a specific permission is required, check if the user has that permission
  if (requiredPermission && !hasPermission(requiredPermission)) {
    return null; // Redirect will happen in useEffect
  }
  
  // If authenticated and has the required role/permission (if any), render the child routes
  return <Outlet />;
};

export default ProtectedRoute;
